import { useEffect, useState } from 'react'

import CircleDashedIcon from '~/components/Icons/svgs/CircleDashedIcon'
import SparklesIcon from '~/components/Icons/svgs/SparklesIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

interface Props {
  className?: string
  showSparkles?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses: Record<Required<Props>['size'], { wrapper: string; brain: string; brainImg: string }> = {
  sm: { wrapper: 'size-44', brain: 'size-28', brainImg: 'w-28' },
  md: { wrapper: 'size-52', brain: 'size-36', brainImg: 'w-36' },
  lg: { wrapper: 'size-60', brain: 'size-40', brainImg: 'w-40' },
}

export default function BrainOrb({ className = '', showSparkles = true, size = 'md' }: Props): React.ReactElement {
  const classes = sizeClasses[size]
  const [sparkleProgress, setSparkleProgress] = useState(0)

  useEffect(() => {
    if (!showSparkles) return undefined

    const interval = setInterval(() => {
      setSparkleProgress((prev) => (prev + 1) % 100)
    }, 50)

    return () => clearInterval(interval)
  }, [showSparkles])

  const sparkles = Array.from({ length: 8 }, (_, i) => {
    const angle = i * 45
    const maxRadius = 220
    const minRadius = 60
    const progress = (sparkleProgress + i * 12.5) % 100
    const radius = minRadius + (progress / 100) * (maxRadius - minRadius)
    const x = Math.cos((angle * Math.PI) / 180) * radius
    const y = Math.sin((angle * Math.PI) / 180) * radius
    const opacity = progress < 90 ? 1 - progress / 100 : 0
    const scale = 0.8 + opacity * 0.4
    return { x, y, opacity, scale, angle }
  })

  return (
    <div aria-hidden className={`relative flex items-center justify-center ${className}`}>
      {showSparkles && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {sparkles.map((sparkle) => (
            <div
              className="absolute transition-all duration-100 ease-linear"
              key={sparkle.angle}
              style={{
                transform: `translate(${sparkle.x}px, ${sparkle.y}px) scale(${sparkle.scale})`,
                opacity: sparkle.opacity,
              }}
            >
              <SVGIcon icon={SparklesIcon} pathClassName="text-purple" size={24} />
            </div>
          ))}
        </div>
      )}

      <div className="absolute bg-gradient-to-r from-purple-dark/30 to-pink-500/30 blur-3xl rounded-full size-full animate-pulse-glow" />

      <div className={`relative z-10 flex items-center justify-center animate-float ${classes.wrapper}`}>
        <div className={`absolute inset-0 animate-spin-slow ${classes.wrapper}`}>
          <CircleDashedIcon className="size-full" />
        </div>
        <div
          className="absolute inset-0 bg-gradient-to-br from-purple-light-deep to-blue-icy blur-2xl rounded-full"
          style={{ animation: 'pulse-glow 2s ease-in-out infinite' }}
        />
        <div className={`relative z-10 drop-shadow-2xl rounded-full ${classes.brain}`}>
          <ResponsiveImage
            height={796}
            imageClassName={classes.brainImg}
            imageWidths="228 456 684"
            loading="eager"
            srcKey="curriculum/mind_brain_ai.png"
            width={900}
          />
        </div>
        <div
          className="absolute inset-12 border-2 border-purple-dark-top rounded-full"
          style={{
            animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
            animationDelay: '0.5s',
          }}
        />
      </div>
    </div>
  )
}
